import { format } from 'date-fns'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { InquiryEvent } from '@/gen/cortado/v1/inquiries_pb'

/**
 * Extracts event details from the first inquiry event
 *
 * Since inquiries now support multiple events, this helper extracts the date/time/guest
 * details from the first event for backwards compatibility in forms and displays.
 *
 * @param events - Array of InquiryEvent objects
 * @returns Object with eventDate, eventLength (hours), eventGuests
 *
 * @example
 * const firstEvent = getFirstEventDetails(inquiry.events)
 * console.log(firstEvent.eventDate)   // Date object or undefined
 * console.log(firstEvent.eventLength) // 2.5 (hours) or 0.5 default
 * console.log(firstEvent.eventGuests)  // 100 or 0 default
 */
export function getFirstEventDetails(events?: InquiryEvent[]): {
  eventDate: Date | undefined
  eventLength: number
  eventGuests: number
  eventTimezone: string | undefined
} {
  const firstEvent = events?.[0]
  if (!firstEvent) {
    return {
      eventDate: undefined,
      eventLength: 0.5,
      eventGuests: 0,
      eventTimezone: undefined,
    }
  }

  return {
    eventDate: timestampToDate(firstEvent.eventDate),
    eventLength: firstEvent.eventLength ?? 0.5,
    eventGuests: firstEvent.eventGuests ?? 0,
    eventTimezone: firstEvent.eventTimezone || undefined,
  }
}

/**
 * Converts a protobuf Timestamp to a JavaScript Date object
 *
 * This utility handles the conversion from protobuf's timestamp format (seconds + nanos)
 * to a native JavaScript Date object. It's useful when you need to perform date calculations
 * or use date-fns functions that require Date objects.
 *
 * @param timestamp - Protobuf Timestamp object with seconds/nanos, or a Date object
 * @returns JavaScript Date object, or undefined if timestamp is invalid/missing
 *
 * @example
 * // Convert protobuf timestamp to Date
 * const eventDate = timestampToDate(inquiry.eventDate)
 * if (eventDate) {
 *   eventDate.setHours(10, 0, 0, 0) // Manipulate as needed
 * }
 *
 * @example
 * // Use with date-fns for calculations
 * const startDate = timestampToDate(event.startTime)
 * const endDate = addHours(startDate, 2)
 */
export function timestampToDate(timestamp?: Timestamp | Date | any): Date | undefined {
  if (!timestamp) return undefined

  // Already a Date object, return as-is
  if (timestamp instanceof Date) {
    return timestamp
  }

  // Convert protobuf timestamp (handles both bigint and number seconds)
  const seconds = typeof timestamp.seconds === 'bigint'
    ? Number(timestamp.seconds)
    : timestamp.seconds

  // Check for valid seconds before creating Date
  // (handles empty timestamp objects with undefined/null seconds)
  if (seconds == null || (typeof seconds === 'number' && isNaN(seconds))) {
    return undefined
  }

  return new Date(seconds * 1000)
}

/**
 * Formats a protobuf Timestamp to a time string in 24-hour format (HH:mm)
 *
 * Use this when you need to display or work with just the time portion of a timestamp,
 * such as in time picker inputs or when showing event start times without the date.
 *
 * @param timestamp - Protobuf Timestamp object, Date object, or undefined
 * @returns Time string in HH:mm format (e.g., "14:30"), or empty string if invalid
 *
 * @example
 * // Display event time in a time input
 * <Input
 *   type="time"
 *   value={formatTimestampToTimeString(inquiry.eventDate)}
 *   onChange={(e) => handleTimeChange(e.target.value)}
 * />
 *
 * @example
 * // Extract time for form default values
 * const form = useForm({
 *   defaultValues: {
 *     eventTime: formatTimestampToTimeString(inquiry.eventDate)
 *   }
 * })
 */
export function formatTimestampToTimeString(timestamp?: Timestamp | Date | any): string {
  if (!timestamp) return ''

  const date = timestampToDate(timestamp)
  return date ? format(date, 'HH:mm') : ''
}

/**
 * Formats decimal hours to a human-readable duration string
 *
 * Converts a numeric duration (in hours) to a readable format like "2 hours 30 minutes".
 * This is commonly used for event lengths, service durations, or any time-based measurements.
 * Handles edge cases like zero duration and rounds minutes to the nearest whole number.
 *
 * @param hours - Duration in decimal hours (e.g., 2.5 = 2 hours 30 minutes)
 * @returns Formatted string with hours and/or minutes, or "0 hours" if duration is zero
 *
 * @example
 * // Display event length
 * formatDuration(2.5)  // Returns: "2 hours 30 minutes"
 * formatDuration(1.0)  // Returns: "1 hour"
 * formatDuration(0.75) // Returns: "45 minutes"
 * formatDuration(0)    // Returns: "0 hours"
 *
 * @example
 * // Use in form displays
 * <Label>Event Duration</Label>
 * <p>{formatDuration(inquiry.eventLength)}</p>
 */
export function formatDuration(hours: number): string {
  if (hours === 0) return '0 hours'

  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)

  const parts: string[] = []
  if (wholeHours > 0) {
    parts.push(`${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'}`)
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)
  }

  return parts.join(' ') || '0 hours'
}

/** One minute in decimal hours: the finest granularity duration inputs accept. */
export const MINUTE_IN_HOURS = 1 / 60

/**
 * Rounds decimal hours to the nearest whole minute, so typed or stepped
 * durations never carry sub-minute float noise (e.g. 1.0166666 -> 61 minutes).
 */
export function roundToMinute(hours: number): number {
  return Math.round(hours * 60) / 60
}

/**
 * Parses a duration string or decimal number back to decimal hours
 *
 * This is the inverse of formatDuration(). It accepts either:
 * - A decimal number as a string (e.g., "2.5")
 * - A formatted duration string (e.g., "2 hours 30 minutes", "1 hour", "45 minutes")
 *
 * Use this when parsing user input from text fields or when converting stored
 * string durations back to numeric values for calculations.
 *
 * @param value - Duration string to parse (decimal or formatted text)
 * @returns Duration in decimal hours (e.g., 2.5 for 2 hours 30 minutes)
 *
 * @example
 * // Parse decimal input
 * parseDuration("2.5")  // Returns: 2.5
 *
 * @example
 * // Parse formatted duration text
 * parseDuration("2 hours 30 minutes")  // Returns: 2.5
 * parseDuration("1 hour")               // Returns: 1.0
 * parseDuration("1 hour 15 minutes")    // Returns: 1.25
 * parseDuration("45 minutes")           // Returns: 0.75
 *
 * @example
 * // Use with form input onBlur
 * <Input
 *   onBlur={(e) => {
 *     const hours = parseDuration(e.target.value)
 *     field.handleChange(Math.max(0.5, hours)) // Enforce minimum
 *   }}
 * />
 */
export function parseDuration(value: string): number {
  // Parse formatted string first (e.g., "1 hour 30 minutes"). A bare decimal
  // like "1.25" contains no hour/minute wording, so this only matches actual
  // formatted output from formatDuration() and avoids parseFloat() truncating
  // it (e.g. "1 hour 15 minutes" -> 1) before the minutes are read.
  //
  // The captured number must be a whole token, not a fragment: `(?:^|[^\d.])`
  // requires the match to start at the string boundary or after a non-digit,
  // non-dot character (so `\s*` can't "skip zero chars" mid-number), and
  // `(?:\.\d+)?` allows an optional decimal part. Without this, "1.5 hours"
  // would fail to match at "1" (`\s*` can't cross the ".") and then match
  // "5 hours", capturing 5 instead of 1.5.
  const hourMatch = value.match(/(?:^|[^\d.])(\d+(?:\.\d+)?)\s*hours?\b/i)
  const minuteMatch = value.match(/(?:^|[^\d.])(\d+(?:\.\d+)?)\s*minutes?\b/i)
  if (hourMatch || minuteMatch) {
    const hours = hourMatch ? parseFloat(hourMatch[1]) : 0
    const minutes = minuteMatch ? parseFloat(minuteMatch[1]) : 0
    return hours + minutes / 60
  }

  // Fall back to decimal parsing (e.g., "1.5", "2.0", "1.5h")
  const decimal = parseFloat(value)
  return isNaN(decimal) ? 0 : decimal
}

/**
 * Calculates a formatted service time range from a start time and duration
 *
 * Given a time string (HH:mm format) and duration in hours, this calculates the
 * end time and returns a formatted range string. Useful for displaying event
 * schedules, booking windows, or service periods.
 *
 * @param eventTime - Start time in HH:mm format (24-hour)
 * @param durationHours - Duration in decimal hours
 * @returns Formatted time range (e.g., "2:00 PM - 4:30 PM"), or null if invalid input
 *
 * @example
 * // Display service window
 * calculateServiceTimeRange("14:00", 2.5)
 * // Returns: "2:00 PM - 4:30 PM"
 *
 * @example
 * // Show in form preview
 * const serviceTime = calculateServiceTimeRange(
 *   form.values.eventTime,
 *   form.values.eventLength
 * )
 * {serviceTime && <p className="text-sm text-muted-foreground">{serviceTime}</p>}
 */
export function calculateServiceTimeRange(eventTime: string, durationHours: number): string | null {
  if (!eventTime || durationHours <= 0) {
    return null
  }

  try {
    const [hours, minutes] = eventTime.split(':').map(Number)
    const startDate = new Date()
    startDate.setHours(hours, minutes, 0, 0)

    const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000)

    const startTime = format(startDate, 'h:mm a')
    const endTime = format(endDate, 'h:mm a')

    return `${startTime} - ${endTime}`
  } catch {
    return null
  }
}

/**
 * Combines a Date object with a time string to create a new Date with that time
 *
 * Takes a Date (for the calendar date) and a time string in HH:mm format (24-hour),
 * and returns a new Date object with the combined date and time.
 *
 * @param date - Date object providing the year, month, and day
 * @param time - Time string in HH:mm format (e.g., "14:30")
 * @returns A new Date object with the combined date and time
 *
 * @example
 * const eventDate = new Date('2026-02-15')
 * const combined = combineDateAndTime(eventDate, '14:30')
 * // Returns: Date object for "Feb 15, 2026 2:30 PM"
 */
export function combineDateAndTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number)
  const combined = new Date(date)
  combined.setHours(hours, minutes, 0, 0)
  return combined
}
